/**
 * Ranked school name search (client-side).
 * Exact / prefix matches first, then word-prefix, contains, then light fuzzy.
 */

export const SEARCH_TIER = {
  EXACT: 0,
  PREFIX: 1,
  WORD_PREFIX: 2,
  CONTAINS: 3,
  FUZZY: 4,
};

const MIN_QUERY_LEN = 2;
const DEFAULT_LIMIT = 12;

/** Lowercase, strip punctuation, collapse spaces; expand st ↔ saint. */
export function normalizeSearchText(input) {
  let s = String(input || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/['’`]/g, "")
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!s) return "";

  // Bidirectional St / Saint so either form matches the other
  s = s
    .replace(/\bst\b/g, "saint")
    .replace(/\bsaint\b/g, "saint");

  return s;
}

function tokens(normalized) {
  return normalized ? normalized.split(" ").filter(Boolean) : [];
}

function schoolHaystacks(school) {
  const names = [school?.name, school?.display_name].filter(Boolean);
  const primary = normalizeSearchText(names[0] || "");
  const alt = names[1] ? normalizeSearchText(names[1]) : "";
  const code = school?.school_code
    ? normalizeSearchText(String(school.school_code))
    : "";
  return { primary, alt, code, words: tokens(primary) };
}

function levenshtein(a, b) {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  if (Math.abs(a.length - b.length) > 2) return 99;

  const prev = new Array(b.length + 1);
  const curr = new Array(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;

  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        prev[j] + 1,
        curr[j - 1] + 1,
        prev[j - 1] + cost
      );
    }
    for (let j = 0; j <= b.length; j++) prev[j] = curr[j];
  }
  return prev[b.length];
}

function fuzzyAllowed(query, candidate) {
  if (query.length < 3 || candidate.length < 3) return false;
  // Same first letter avoids noise like dover ↔ roger (edit distance 2).
  if (query[0] !== candidate[0]) return false;
  const maxDist = query.length <= 4 ? 1 : 2;
  return levenshtein(query, candidate) <= maxDist;
}

/**
 * Score one school against a normalized query.
 * Lower tier = better. Returns null if no match.
 */
export function scoreSchoolMatch(school, normalizedQuery) {
  if (!normalizedQuery) return null;

  const { primary, alt, code, words } = schoolHaystacks(school);
  if (!primary && !alt && !code) return null;

  const q = normalizedQuery;
  const qWords = tokens(q);

  const texts = [primary, alt].filter(Boolean);

  for (const text of texts) {
    if (text === q) {
      return { tier: SEARCH_TIER.EXACT, tieBreak: 0 };
    }
  }
  if (code && code === q) {
    return { tier: SEARCH_TIER.EXACT, tieBreak: 0 };
  }

  for (const text of texts) {
    if (text.startsWith(q)) {
      return { tier: SEARCH_TIER.PREFIX, tieBreak: text.length };
    }
  }

  for (const text of texts) {
    const w = tokens(text);
    if (w.some((word) => word.startsWith(q))) {
      return { tier: SEARCH_TIER.WORD_PREFIX, tieBreak: text.length };
    }
    // multi-word query: every query word starts some school word
    if (
      qWords.length > 1 &&
      qWords.every((qw) => w.some((word) => word.startsWith(qw)))
    ) {
      return { tier: SEARCH_TIER.WORD_PREFIX, tieBreak: text.length + 1 };
    }
  }

  for (const text of texts) {
    if (text.includes(q)) {
      return { tier: SEARCH_TIER.CONTAINS, tieBreak: text.indexOf(q) };
    }
  }
  if (code && code.includes(q)) {
    return { tier: SEARCH_TIER.CONTAINS, tieBreak: 0 };
  }

  // Fuzzy: against full name or individual words
  for (const text of texts) {
    if (fuzzyAllowed(q, text)) {
      return {
        tier: SEARCH_TIER.FUZZY,
        tieBreak: levenshtein(q, text),
      };
    }
  }
  for (const word of words) {
    if (fuzzyAllowed(q, word)) {
      return {
        tier: SEARCH_TIER.FUZZY,
        tieBreak: levenshtein(q, word) + 1,
      };
    }
  }

  return null;
}

/**
 * @param {object[]} schools
 * @param {string} query
 * @param {{ limit?: number, minQueryLength?: number }} [options]
 * @returns {{ school: object, tier: number, tieBreak: number }[]}
 */
export function searchSchools(schools, query, options = {}) {
  const limit = options.limit ?? DEFAULT_LIMIT;
  const minLen = options.minQueryLength ?? MIN_QUERY_LEN;
  const q = normalizeSearchText(query);

  if (!q || q.length < minLen || !Array.isArray(schools)) return [];

  const scored = [];
  for (const school of schools) {
    const match = scoreSchoolMatch(school, q);
    if (match) scored.push({ school, ...match });
  }

  scored.sort((a, b) => {
    if (a.tier !== b.tier) return a.tier - b.tier;
    if (a.tieBreak !== b.tieBreak) return a.tieBreak - b.tieBreak;
    const an = String(a.school?.name || "");
    const bn = String(b.school?.name || "");
    return an.localeCompare(bn);
  });

  return scored.slice(0, limit);
}

/**
 * Filter a school list by ranked search. If the query is inactive (too short),
 * returns the original list unchanged (safe for left-panel buckets).
 */
export function applySchoolSearch(schools, query, options = {}) {
  const minLen = options.minQueryLength ?? MIN_QUERY_LEN;
  if (!Array.isArray(schools)) return [];
  if (String(query || "").trim().length < minLen) return schools;

  return searchSchools(schools, query, {
    limit: schools.length,
    minQueryLength: minLen,
  }).map((hit) => hit.school);
}

export function isSchoolSearchActive(query, minLen = MIN_QUERY_LEN) {
  return String(query || "").trim().length >= minLen;
}
